import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@shared/services/api";
import type { CategoryTreeResponse, CategoryOption, CategoryTreeNode } from "@shared/types/categoryTree";

// Hook para obtener categorías en estructura de árbol
export function useCategoryTreeData(catalogId?: string) {
  return useQuery({
    queryKey: ["categoryTree", catalogId],
    queryFn: async (): Promise<CategoryTreeResponse> => {
      const url = catalogId 
        ? `/category/tree?catalogId=${catalogId}`
        : "/category/tree";
      
      return await apiClient.get<CategoryTreeResponse>(url);
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
  });
}

// Función para convertir árbol de categorías a opciones de desplegable
export function categoryTreeToOptions(
  categories: CategoryTreeNode[],
  level = 0
): CategoryOption[] {
  const options: CategoryOption[] = [];

  categories.forEach((category) => {
    // Agregar la categoría actual
    options.push({
      value: category.id,
      label: level > 0 ? "  ".repeat(level) + category.name : category.name,
      level,
      isRoot: category.isRootCategory,
    });

    // Agregar recursivamente las subcategorías
    if (category.children && category.children.length > 0) {
      const childOptions = categoryTreeToOptions(category.children, level + 1);
      options.push(...childOptions);
    }
  });

  return options;
}

// Hook que devuelve las opciones formateadas para desplegables
export function useCategoryOptions(catalogId?: string) {
  const { data, isLoading, error } = useCategoryTreeData(catalogId);

  const options: CategoryOption[] = data?.data 
    ? categoryTreeToOptions(data.data)
    : [];

  return {
    options,
    isLoading,
    error,
    categories: data?.data || [],
  };
}
